import { Link } from 'react-router-dom';

import { CmsSectionShell } from './CmsSectionShell.tsx';
import { CmsSectionState } from './CmsSectionState.tsx';
import { themeOf, EYEBROW } from './cmsTheme.ts';
import { useFestivalData } from '../../lib/festivalData.ts';
import { cdnVariant, cdnSrcSet } from '../../lib/cdn.ts';
import { newsStoryPath } from '../../lib/pages.ts';
import { formatNewsDate } from '../../application/festivalSelectors.ts';
import type { PinnedNewsSection } from '../../lib/types.ts';

/**
 * Renders a single pinned news article as a wide teaser card (CMS-039).
 * The article is resolved by ID from news.json. If the article is not found,
 * the section auto-hides (returns null). Image position (left/right) is configurable.
 *
 * Same v2 treatment as the news grid: a 3:2 image against the section image tone, the copy on
 * the section card shade, and the date as an accent eyebrow.
 */
export function CmsPinnedNewsSection({ theme, guitar, articleId, imageSide = 'left' }: PinnedNewsSection) {
  const t = themeOf(theme);
  const { data, loading, error } = useFestivalData();

  // Resolve the article by ID
  const article = data?.news.items.find(item => item.id === articleId);

  // Auto-hide if article not found or unpublished
  if (!article) {
    return null;
  }

  return (
    <CmsSectionShell theme={theme} guitar={guitar}>
      <CmsSectionState
        loading={loading}
        error={!!error}
        loadingLabel="Indlæser artikel…"
        bodyClass={t.body}
        errorMessage="Artiklen kunne ikke indlæses."
      >
        <Link
          to={newsStoryPath(article)}
          className={`group block overflow-hidden rounded-card ${t.card.bg} ${t.card.text}`}
        >
          <div className={`grid lg:grid-cols-2 items-stretch ${imageSide === 'right' ? 'lg:[&>*:first-child]:order-last' : ''}`}>
            {/* Image side */}
            {article.imageUrl && (
              <div className={`aspect-3/2 lg:aspect-auto lg:min-h-[22rem] overflow-hidden ${t.img}`}>
                <img
                  src={cdnVariant(article.imageUrl, 'mobile')}
                  srcSet={cdnSrcSet(article.imageUrl) || undefined}
                  sizes="(min-width: 1024px) 50vw, 100vw"
                  alt={article.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  loading="lazy"
                />
              </div>
            )}

            {/* Text side */}
            <div className="flex flex-col justify-center p-6 md:p-10">
              <div className={`${EYEBROW} ${t.eyebrow} mb-3`}>
                {formatNewsDate(article.publishDate)}
              </div>
              <h2 className="font-display text-3xl md:text-[40px] leading-none tracking-[-0.02em] mb-4 text-balance">
                {article.title}
              </h2>
              {article.htmlContent && (
                <div
                  className={`text-base md:text-lg leading-relaxed ${t.card.muted} mb-6 line-clamp-3`}
                  dangerouslySetInnerHTML={{
                    __html: stripHtmlTags(article.htmlContent).substring(0, 200) + '…',
                  }}
                />
              )}
              <div className="inline-flex items-center gap-2 font-medium">
                Læs artikel
                <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </div>
            </div>
          </div>
        </Link>
      </CmsSectionState>
    </CmsSectionShell>
  );
}

function stripHtmlTags(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}
